import React from 'react'
import {
  Application,
  AttackEventDetails,
  HttpRequest,
  Server,
  UserInput,
  RuleName
} from './shared'
import { AttackStatus } from '../attack-status'
import {
  CmdInjectionChildRuleSemanticAnalysisOverview,
  CmdInjectionFindings,
  CmdInjectionParentRuleSemanticAnalysisOverview,
  ICommandInjectionAnalysisOverviewDetails
} from './attack-event-rules/cmd-injection-semantic-analysis-overview'
import { PathTraversalSemanticAnalysisOverview } from './attack-event-rules/path-traversal-semantic-analysis-overview'
import { bodyCss } from '../../legacy-design-system/text'
import { AttackEventStatusIndicator } from './attack-event-status-indicator'
import { css } from '@emotion/css'
import { ZipFileOverwriteOverview } from './attack-event-rules/zip-file-overwrite-overview'
import { UnsafeFileUploadOverview } from './attack-event-rules/unsafe-file-upload-overview'
import {
  ISqlInjectionAnalysisOverviewDetails,
  SqlInjectionChildRuleSemanticAnalysisOverview,
  SqlInjectionFindings,
  SqlInjectionParentRuleSemanticAnalysisOverview
} from './attack-event-rules/sql-injection-semantic-analysis-overview'
import { FormattedMessage } from 'react-intl'
import {
  PathTraversalFileSecurityBypassSemanticAnalysisOverview,
  isPathTraversalFileSecurityBypassSemanticAnalysisProps
} from './attack-event-rules/path-traversal-file-security-bypass-semantic-analysis-overview'
import { CodeSnippet } from '../../legacy-design-system/code-snippet'
import { WithRedactedTooltip } from './with-redacted-tooltip'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { ContrastFormattedMessage } from '../contrast-formatted-message'

export const isNonMustacheOverview = ({
  ruleUuid,
  detectionType
}: {
  readonly ruleUuid: string
  readonly detectionType?: string
}) =>
  ruleUuid === 'zip-file-overwrite' ||
  ruleUuid === 'unsafe-file-upload' ||
  (detectionType && detectionType === 'SEMANTIC_ANALYSIS')

interface OverviewProps {
  readonly httpRequest?: HttpRequest
  readonly userInput?: UserInput
  readonly result: AttackStatus
  readonly sourceIP?: string
  readonly callLocation?: string
  readonly ruleUuid: string
  readonly detectionType?: string
}

interface RuleSpecificOverviewProps extends OverviewProps {
  readonly details?: AttackEventDetails
}

interface SqlInjectionOverviewProps extends OverviewProps {
  readonly details: ISqlInjectionAnalysisOverviewDetails
}

interface CommandInjectionOverviewProps extends OverviewProps {
  readonly details: ICommandInjectionAnalysisOverviewDetails
}

export interface AttackEventOverviewViewProps extends OverviewProps {
  readonly details?: AttackEventDetails
  readonly organizationID: string
  readonly application: Application
  readonly server: Server
}

export function AttackEventOverviewView({
  httpRequest,
  userInput,
  details,
  application,
  server,
  result,
  sourceIP,
  organizationID,
  ruleUuid,
  detectionType,
  callLocation
}: AttackEventOverviewViewProps) {
  return (
    <div className={bodyCss}>
      <div
        className={css`
          margin-bottom: 10px;
          word-break: normal;
        `}
      >
        <RuleSpecificOverview
          result={result}
          ruleUuid={ruleUuid}
          detectionType={detectionType}
          httpRequest={httpRequest}
          userInput={userInput}
          details={details}
          sourceIP={sourceIP}
          callLocation={callLocation}
        />
      </div>
      <AttackEventStatusIndicator
        result={result}
        organizationID={organizationID}
        application={application}
        server={server}
      />
    </div>
  )
}

function isSemanticSqlInjectionChildRule(
  expectedRuleUuid: string,
  comparedRuleUuid: string,
  detectionType?: string,
  details?: AttackEventDetails
) {
  return (
    expectedRuleUuid === comparedRuleUuid &&
    detectionType === 'SEMANTIC_ANALYSIS' &&
    !!details &&
    !!details.query
  )
}

function isSemanticCommandInjectionChildRule(
  expectedRuleUuid: string,
  comparedRuleUuid: string,
  detectionType?: string,
  details?: AttackEventDetails
) {
  return (
    expectedRuleUuid === comparedRuleUuid &&
    detectionType === 'SEMANTIC_ANALYSIS' &&
    !!details &&
    !!details.command
  )
}

function RuleSpecificOverview(props: RuleSpecificOverviewProps) {
  const {
    ruleUuid,
    detectionType,
    httpRequest,
    userInput,
    details,
    sourceIP,
    callLocation,
    result
  } = props

  if (
    isSemanticSqlInjectionChildRule(
      RuleName.SQL_INJECTION_CHAINED_QUERIES,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <SqlInjectionChildRuleSemanticAnalysisOverview
        {...(props as SqlInjectionOverviewProps)}
        sqlInjectionFinding={SqlInjectionFindings.CHAINING}
      />
    )
  }
  if (
    isSemanticSqlInjectionChildRule(
      RuleName.SQL_INJECTION_TAUTOLOGIES,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <SqlInjectionChildRuleSemanticAnalysisOverview
        {...(props as SqlInjectionOverviewProps)}
        sqlInjectionFinding={SqlInjectionFindings.TAUTOLOGIES}
      />
    )
  }
  if (
    isSemanticSqlInjectionChildRule(
      RuleName.SQL_INJECTION_SUSPICIOUS_UNIONS,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <SqlInjectionChildRuleSemanticAnalysisOverview
        {...(props as SqlInjectionOverviewProps)}
        sqlInjectionFinding={SqlInjectionFindings.SUSPICIOUS_UNIONS}
      />
    )
  }
  if (
    isSemanticSqlInjectionChildRule(
      RuleName.SQL_INJECTION_DANGEROUS_FUNCTIONS,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <SqlInjectionChildRuleSemanticAnalysisOverview
        {...(props as SqlInjectionOverviewProps)}
        sqlInjectionFinding={SqlInjectionFindings.DANGEROUS_FUNCTIONS}
      />
    )
  }
  if (
    ruleUuid === RuleName.SQL_INJECTION &&
    detectionType === 'SEMANTIC_ANALYSIS' &&
    !!details &&
    !!details.query &&
    !!details.findings
  ) {
    return (
      <SqlInjectionParentRuleSemanticAnalysisOverview
        details={details as ISqlInjectionAnalysisOverviewDetails}
        httpRequest={httpRequest}
        userInput={userInput}
        sourceIP={sourceIP}
        result={result}
      />
    )
  }
  if (
    isSemanticCommandInjectionChildRule(
      RuleName.CMD_INJECTION_CHAINED_COMMANDS,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <CmdInjectionChildRuleSemanticAnalysisOverview
        {...(props as CommandInjectionOverviewProps)}
        cmdInjectionFinding={CmdInjectionFindings.CHAINING}
      />
    )
  }
  if (
    isSemanticCommandInjectionChildRule(
      RuleName.CMD_INJECTION_BACKDOORS,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <CmdInjectionChildRuleSemanticAnalysisOverview
        {...(props as CommandInjectionOverviewProps)}
        cmdInjectionFinding={CmdInjectionFindings.BACKDOOR}
      />
    )
  }
  if (
    isSemanticCommandInjectionChildRule(
      RuleName.CMD_INJECTION_DANGEROUS_PATHS,
      ruleUuid,
      detectionType,
      details
    )
  ) {
    return (
      <CmdInjectionChildRuleSemanticAnalysisOverview
        {...(props as CommandInjectionOverviewProps)}
        cmdInjectionFinding={CmdInjectionFindings.PATH_ARGUMENT}
      />
    )
  }
  if (
    ruleUuid === RuleName.CMD_INJECTION &&
    detectionType === 'SEMANTIC_ANALYSIS' &&
    !!details
  ) {
    return (
      <CmdInjectionParentRuleSemanticAnalysisOverview
        details={details}
        httpRequest={httpRequest}
        userInput={userInput}
        sourceIP={sourceIP}
        result={result}
      />
    )
  }
  if (
    ruleUuid === RuleName.PATH_TRAVERSAL &&
    detectionType === 'SEMANTIC_ANALYSIS' &&
    !!details
  ) {
    return (
      <PathTraversalSemanticAnalysisOverview
        details={details}
        httpRequest={httpRequest}
        userInput={userInput}
        sourceIP={sourceIP}
        callLocation={callLocation}
        result={result}
      />
    )
  }

  if (
    isPathTraversalFileSecurityBypassSemanticAnalysisProps(
      ruleUuid,
      detectionType,
      details,
      callLocation
    )
  ) {
    return (
      <PathTraversalFileSecurityBypassSemanticAnalysisOverview
        details={details}
        httpRequest={httpRequest}
        userInput={userInput}
        sourceIP={sourceIP}
        callLocation={callLocation}
        result={result}
      />
    )
  }
  if (ruleUuid === RuleName.ZIP_FILE_OVERWRITE) {
    return (
      <React.Fragment>
        {details && (
          <ZipFileOverwriteOverview
            details={{
              entryName: !!details.entryName ? details.entryName : ''
            }}
          />
        )}
      </React.Fragment>
    )
  }
  if (ruleUuid === RuleName.UNSAFE_FILE_UPLOAD) {
    return <UnsafeFileUploadOverview userInput={userInput} />
  }
  return <div />
}

/**
 * This component is used to display the suspicious text for the old, legacy semantic analysis rules.
 */
export function LegacySuspiciousEventDescription({
  containerCss,
  result
}: {
  readonly result: AttackStatus
  readonly containerCss?: string
}) {
  return (
    <React.Fragment>
      {result === AttackStatus.SUSPICIOUS && (
        <div className={containerCss ? containerCss : undefined}>
          <FormattedMessage
            id="detail_attackEventSuspicious_investigateAttack"
            values={{
              suspiciousText: (
                <b>
                  <ContrastFormattedMessage
                    id="title_attackStatusSuspicious"
                    children={(msg) => msg.toLowerCase()}
                  />
                </b>
              )
            }}
          />
        </div>
      )}
    </React.Fragment>
  )
}

/**
 * This component is used to display the suspicious text for the new, separate semantic analysis rules.
 */
export function NewSuspiciousEventDescription({
  findingTranslationId,
  containerCss
}: {
  readonly findingTranslationId: string
  readonly containerCss?: string
}) {
  return (
    <React.Fragment>
      {
        <div className={containerCss}>
          <span
            className={css`
              padding-right: 3px;
            `}
          >
            <FormattedMessage
              id="detail_attackEventSuspicious_investigateAttack"
              values={{
                suspiciousText: (
                  <b>
                    <ContrastFormattedMessage
                      id="title_attackStatusSuspicious"
                      children={(msg) => msg.toLowerCase()}
                    />
                  </b>
                )
              }}
            />
          </span>
          <FormattedMessage
            id="detail_attackEventSuspicious_createExclusionToIgnore"
            values={{
              codeExclusionText: (
                <b>
                  <FormattedMessage id="detail_attackEventSuspicious_investigateAttackCodeExclusion" />
                </b>
              ),
              findingText: (
                <b>
                  <ContrastFormattedMessage
                    id={findingTranslationId}
                    children={(msg) => msg.toLowerCase()}
                  />
                </b>
              )
            }}
          />
        </div>
      }
    </React.Fragment>
  )
}

/**
 * This component is used to display the attack event result. If specified, it
 * will highlight the specific part of the result using the start and end index, otherwise
 * it will highlight the entire result
 */
export function AttackEventResultCodeSnippet({
  result,
  start,
  end
}: {
  readonly result: string
  // The start index of the result that should be be highlighted
  readonly start?: number
  // The end index of the result that should be be highlighted
  readonly end?: number
}) {
  return start && end && start <= end && end + 1 <= result.length ? (
    <CodeSnippet>
      <WithRedactedTooltip text={result.substring(0, start)} />
      <span
        className={css`
          color: ${theme.color.red50};
        `}
      >
        <WithRedactedTooltip text={result.substring(start, end + 1)} />
      </span>
      <WithRedactedTooltip text={result.substring(end + 1)} />
    </CodeSnippet>
  ) : (
    <CodeSnippet
      className={css`
        color: ${theme.color.red50};
      `}
    >
      <WithRedactedTooltip text={result} />
    </CodeSnippet>
  )
}
